import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { type Line, type Point } from "./../types";

type LineFormInput = {
  pointAId: string,
  pointBId: string
};

export default function LineForm ({
  points,
  lines,
  onSubmit,
  onRemove
}: {
  points: Point[],
  lines: Line[],
  onSubmit: (line: Line) => void
  onRemove: (id: string) => void
}) {
  const [status, setStatus] = useState("Ready to add a line.");
  const { clearErrors, handleSubmit, register, setError, formState } = useForm<LineFormInput>();
  const onSubmitLine: SubmitHandler<LineFormInput> = (data) => {
    const pointA = points.find((p) => p.id === data.pointAId);
    const pointB = points.find((p) => p.id === data.pointBId);

    if (!pointA || !pointB) {
      setStatus("Select two existing points.");
      setError("pointAId", { type: "validate" });
      setError("pointBId", { type: "validate" });
      return;
    }

    if (data.pointAId === data.pointBId) {
      setStatus("Choose two different points.");
      setError("pointAId", { type: "validate" });
      setError("pointBId", { type: "validate" });
      return;
    }

    const duplicateLine = lines.some((line) => {
      const sameDirection = line.pointA.id === data.pointAId && line.pointB.id === data.pointBId;
      const oppositeDirection = line.pointA.id === data.pointBId && line.pointB.id === data.pointAId;
      return sameDirection || oppositeDirection;
    });

    if (duplicateLine) {
      setStatus("This line already exists.");
      setError("pointAId", { type: "validate" });
      setError("pointBId", { type: "validate" });
      return;
    }

    const newLine = {
      pointA,
      pointB,
      id: self.crypto.randomUUID()
    };

    clearErrors();
    onSubmit(newLine);
    setStatus(`Line ${pointA.name}${pointB.name} added.`);
  };

  const onInvalidLine = () => {
    setStatus("Select two points to create a line.");
  };

  return (
    <div className="line-container">
      <ul>
        { lines.map(l => (
          <li key={l.id}>
            Line between {l.pointA.name} -{">"} {l.pointB.name}
            <button onClick={() => onRemove(l.id)}>Remove</button>
          </li>
        ))}
        </ul>
      <hr />
      <form onSubmit={handleSubmit(onSubmitLine, onInvalidLine)}>
        <p className="form-status" role="status">{status}</p>
        Create line between 
        <label>
          point 
          <select
            aria-invalid={Boolean(formState.errors.pointAId)}
            className={formState.errors.pointAId ? "is-invalid" : undefined}
            {...register("pointAId", { required: true })}
          >
            { points.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          and 
        </label>
        <label>
          <br />point 
          <select
            aria-invalid={Boolean(formState.errors.pointBId)}
            className={formState.errors.pointBId ? "is-invalid" : undefined}
            {...register("pointBId", { required: true })}
          >
            { points.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <input type="submit" value="Add line" />
      </form>
    </div>

  );
}
